import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import Stars from "@/components/Stars";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BadgeCheck, MapPin, Award, Briefcase, ArrowRight, Mail, Phone, Info, Globe } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function TradespersonProfilePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tp, setTp] = useState(null);

  useEffect(() => {
    api.get(`/tradespeople/${id}`).then((r) => setTp(r.data)).catch(() => setTp(false));
  }, [id]);

  if (tp === null) return <div className="max-w-5xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;
  if (!tp) return <div className="max-w-5xl mx-auto px-4 py-16 text-slate-500">Profile not found.</div>;

  const isUnclaimed = tp.claim_status === "unclaimed";

  // ---- SEO ----
  const title = `${tp.business_name || tp.name} — ${tp.categories?.[0] || "Tradesperson"} in ${tp.city}, ${tp.province} | Fixus`;
  const description = `${tp.business_name || tp.name} is a ${(tp.categories || []).join(", ").toLowerCase()} business in ${tp.city}, ${tp.province}. ${tp.review_count ? `${tp.rating}★ from ${tp.review_count} reviews. ` : ""}Get a free quote on Fixus.`;
  const ldType = pickLdType(tp.categories?.[0]);
  const ld = {
    "@context": "https://schema.org",
    "@type": ldType,
    "name": tp.business_name || tp.name,
    "description": tp.bio || description,
    "telephone": tp.phone || undefined,
    "email": tp.email || undefined,
    "url": tp.website || undefined,
    "address": tp.address ? {
      "@type": "PostalAddress",
      "addressLocality": tp.city,
      "addressRegion": tp.province,
      "addressCountry": "CA",
      "streetAddress": tp.address,
    } : {
      "@type": "PostalAddress",
      "addressLocality": tp.city,
      "addressRegion": tp.province,
      "addressCountry": "CA",
    },
    "aggregateRating": tp.review_count ? {
      "@type": "AggregateRating",
      "ratingValue": tp.rating,
      "reviewCount": tp.review_count,
      "bestRating": 5,
      "worstRating": 1,
    } : undefined,
  };
  // strip undefined/empty
  Object.keys(ld).forEach((k) => { if (ld[k] === undefined) delete ld[k]; });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="profile" />
        {isUnclaimed && <meta name="robots" content="noindex" />}
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>
      {isUnclaimed && (
        <ClaimBanner tp={tp} />
      )}
      <Card className="border-slate-200">
        <CardContent className="p-8">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-900" data-testid="profile-name">
                  {tp.name}
                </h1>
                {tp.verified && (
                  <Badge data-testid="profile-verified" className="bg-blue-50 text-blue-900 border-blue-200 hover:bg-blue-50 gap-1">
                    <BadgeCheck className="w-3.5 h-3.5" /> Verified
                  </Badge>
                )}
              </div>
              {tp.business_name && (
                <div className="text-lg text-slate-700 mt-1">{tp.business_name}</div>
              )}
              <div className="flex items-center gap-1.5 mt-3 text-sm text-slate-500">
                <MapPin className="w-4 h-4" /> {tp.city}, {tp.province}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {(tp.categories || []).map((c) => (
                  <Badge key={c} variant="outline" className="border-slate-200 text-slate-700 font-normal">
                    {c}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="text-right">
              <Stars value={tp.rating || 0} size={20} showValue />
              <div className="text-sm text-slate-500 mt-1">
                {tp.review_count || 0} review{tp.review_count === 1 ? "" : "s"}
              </div>
              <Button
                data-testid="request-quote-cta"
                onClick={() => nav("/post-job")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white"
              >
                Request a Quote <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          <Separator className="my-7" />

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                <Briefcase className="w-3.5 h-3.5" /> Experience
              </div>
              <div className="text-2xl font-display font-semibold text-slate-900">
                {tp.years_experience || 0} <span className="text-base text-slate-600 font-normal">years</span>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                <Award className="w-3.5 h-3.5" /> Certifications
              </div>
              <div className="flex flex-wrap gap-2">
                {(tp.certifications || []).map((c) => (
                  <Badge key={c} variant="outline" className="border-blue-200 text-blue-900 font-normal">
                    {c}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {(tp.email || tp.phone || tp.website) && (
            <>
              <Separator className="my-7" />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Contact</div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-6">
                  {tp.phone && (
                    <a
                      href={`tel:${tp.phone.replace(/[^+0-9]/g, "")}`}
                      data-testid="profile-phone"
                      className="inline-flex items-center gap-2 text-slate-800 hover:text-blue-900 transition-colors"
                    >
                      <span className="w-9 h-9 rounded-md bg-blue-50 text-blue-900 grid place-items-center">
                        <Phone className="w-4 h-4" />
                      </span>
                      <span className="font-medium">{tp.phone}</span>
                    </a>
                  )}
                  {tp.email && (
                    <a
                      href={`mailto:${tp.email}`}
                      data-testid="profile-email"
                      className="inline-flex items-center gap-2 text-slate-800 hover:text-blue-900 transition-colors"
                    >
                      <span className="w-9 h-9 rounded-md bg-blue-50 text-blue-900 grid place-items-center">
                        <Mail className="w-4 h-4" />
                      </span>
                      <span className="font-medium break-all">{tp.email}</span>
                    </a>
                  )}
                  {tp.website && (
                    <a
                      href={tp.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="profile-website"
                      className="inline-flex items-center gap-2 text-slate-800 hover:text-blue-900 transition-colors"
                    >
                      <span className="w-9 h-9 rounded-md bg-blue-50 text-blue-900 grid place-items-center">
                        <Globe className="w-4 h-4" />
                      </span>
                      <span className="font-medium break-all">Visit website</span>
                    </a>
                  )}
                </div>
              </div>
            </>
          )}

          <Separator className="my-7" />

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">About</div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{tp.bio}</p>
          </div>

          <Separator className="my-7" />

          <div>
            <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
              Reviews ({(tp.reviews || []).length})
            </h2>
            <div className="space-y-4">
              {(tp.reviews || []).map((r, i) => {
                const reviewKey = r.id || `${r.reviewer}-${r.created_at || i}`;
                return (
                  <div
                    key={reviewKey}
                    data-testid={`review-${reviewKey}`}
                    className="p-5 rounded-lg bg-slate-50 border border-slate-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-900">{r.reviewer}</div>
                      <Stars value={r.rating} />
                    </div>
                    <p className="text-slate-700 mt-2 text-sm leading-relaxed">{r.comment}</p>
                  </div>
                );
              })}
              {(tp.reviews || []).length === 0 && (
                <div className="text-slate-500 text-sm">No reviews yet.</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function pickLdType(category) {
  const map = {
    "Plumbing": "Plumber",
    "Electrical": "Electrician",
    "Roofing": "RoofingContractor",
    "HVAC / Heating & Cooling": "HVACBusiness",
    "Painting (Interior / Exterior)": "HousePainter",
    "Carpentry & Woodwork": "GeneralContractor",
    "Home Renovation (General)": "GeneralContractor",
    "Landscaping & Outdoor": "Landscaper",
    "Moving & Hauling": "MovingCompany",
    "Cleaning & Junk Removal": "HouseSitter",
  };
  return map[category] || "LocalBusiness";
}

function ClaimBanner({ tp }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const listingHasEmail = !!tp.email;

  const startClaim = async (claimerEmail) => {
    setSending(true);
    try {
      const payload = { tradesperson_id: tp.id };
      if (claimerEmail) payload.email = claimerEmail;
      const { data } = await api.post("/claims/start", payload);
      toast.success(`Verification email sent to ${data.email_sent_to}. Check your inbox.`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start claim.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="unclaimed-banner"
      className="mb-6 rounded-xl border border-dashed border-orange-300 bg-orange-50 p-5 flex flex-wrap items-center gap-4"
    >
      <span className="w-10 h-10 rounded-md bg-white border border-orange-200 grid place-items-center text-orange-600 shrink-0">
        <Info className="w-5 h-5" />
      </span>
      <div className="flex-1 min-w-[220px]">
        <div className="font-display font-semibold text-slate-900">
          Are you the owner of {tp.business_name || tp.name}?
        </div>
        <div className="text-sm text-slate-600 mt-0.5">
          Claim this listing to receive job leads and add a Verified badge.
          {listingHasEmail ? " We'll email a one-time link to the address on file." : " Enter your business email and we'll send a one-time verification link."}
        </div>
      </div>

      {listingHasEmail ? (
        <Button
          data-testid="claim-listing-btn"
          onClick={() => startClaim()}
          disabled={sending}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          {sending ? "Sending…" : "Claim this listing"}
        </Button>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="claim-listing-btn"
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Claim this listing
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Verify your business email</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 -mt-2">
              We&apos;ll send a one-time claim link to this email. Please use an address you control at {tp.business_name || tp.name}.
            </p>
            <div className="mt-2">
              <Label>Your business email</Label>
              <Input
                data-testid="claim-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbusiness.ca"
                className="mt-1.5"
              />
            </div>
            <DialogFooter>
              <Button
                data-testid="claim-email-submit"
                onClick={() => startClaim(email)}
                disabled={sending || !email}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {sending ? "Sending…" : "Send claim link"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
